import {Search} from "lucide-react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {Loader} from "@/src/modules/core/components";
import {cn} from "@/lib/utils";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";

import {useHouseholdAssistSearch} from "../hooks/use-household-assist-search";
import {applyHouseholdAssistCandidate} from "../lib/apply-household-assist-candidate";
import {DISPATCH_DESK_START_SHIFT_MESSAGE} from "../lib/dispatch-desk-layout";
import {HOUSEHOLD_ASSIST_MIN_QUERY_LENGTH} from "../lib/household-assist";
import {
  useDispatchDeskHouseholdMatch,
  useDispatchDeskHouseholdMatchStore,
} from "../store/desk-household-match";
import type {HouseholdAssistCandidate, HouseholdAssistResult} from "../types/household-assist";

/** Floating results card — explicit shadow so it reads above desk content. */
const FINDER_RESULTS_PANEL_CLASS =
  "border-border bg-card absolute top-full right-0 left-0 z-50 mt-1.5 flex flex-col gap-2 rounded-lg border p-3 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.35)]";

const MIN_QUERY_TOOLTIP = `Enter at least ${HOUSEHOLD_ASSIST_MIN_QUERY_LENGTH} characters to search.`;

type DispatchHouseholdFinderProps = {
  showStartShiftHint?: boolean;
  className?: string;
};

function CandidateRow({
  candidate,
  onSelect,
}: {
  candidate: HouseholdAssistCandidate;
  onSelect: (candidate: HouseholdAssistCandidate) => void;
}) {
  return (
    <button
      type="button"
      className="hover:bg-muted/50 flex w-full flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-colors"
      onClick={() => onSelect(candidate)}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 flex-1 text-sm font-medium leading-snug">{candidate.display_label}</span>
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{candidate.match_score}%</span>
      </div>
      {candidate.matched_on.length > 0 ? (
        <p className="text-muted-foreground line-clamp-2 text-xs">{candidate.matched_on.join(" · ")}</p>
      ) : null}
    </button>
  );
}

function AssistResultsPanel({
  result,
  onSelect,
}: {
  result: HouseholdAssistResult;
  onSelect: (candidate: HouseholdAssistCandidate) => void;
}) {
  if (result.status === "ambiguous" && result.candidates.length > 0) {
    const facetParts: string[] = [];
    if (result.facets.properties?.length) {
      facetParts.push(
        result.facets.properties.map((p) => `${p.name} (${p.count})`).join(", "),
      );
    }
    if (result.facets.streets?.length) {
      facetParts.push(result.facets.streets.map((s) => `${s.street} (${s.count})`).join(", "));
    }

    return (
      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-xs">
          {result.total_count} possible match{result.total_count === 1 ? "" : "es"} — confirm with caller:
        </p>
        {facetParts.length > 0 ? (
          <p className="text-muted-foreground text-xs">{facetParts.join(" · ")}</p>
        ) : null}
        <div className="max-h-[18rem] overflow-y-auto pr-0.5">
          <div className="flex flex-col gap-1.5">
            {result.candidates.map((candidate) => (
              <CandidateRow key={candidate.house_hold_id} candidate={candidate} onSelect={onSelect} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (result.status === "unique" && result.candidates[0]) {
    return (
      <p className="text-muted-foreground text-xs">
        Matched <span className="text-foreground font-medium">{result.candidates[0].display_label}</span>
      </p>
    );
  }

  if (result.suggested_questions.length > 0) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-muted-foreground text-xs font-medium">
          {result.status === "too_many" ? "Too many matches — ask:" : "Need more detail — ask:"}
        </p>
        <ul className="text-muted-foreground flex list-disc flex-col gap-1 pl-4 text-xs">
          {result.suggested_questions.map((question) => (
            <li key={question}>{question}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <p className="text-muted-foreground text-sm">No households matched. Try adding a house number, street, or PIN.</p>
  );
}

export function DispatchHouseholdFinder({
  showStartShiftHint = false,
  className,
}: DispatchHouseholdFinderProps) {
  const me = useStoredMe();
  const userProperty = useStoredUserProperty();
  const location = useLocation();
  const navigate = useNavigate();
  const matchedHousehold = useDispatchDeskHouseholdMatch();

  const [query, setQuery] = useState("");
  const [lastResult, setLastResult] = useState<HouseholdAssistResult | null>(null);
  const [resultsDismissed, setResultsDismissed] = useState(false);
  const [minLengthTipOpen, setMinLengthTipOpen] = useState(false);
  const finderRef = useRef<HTMLDivElement>(null);

  const {search, isPending, validationError, isRateLimited, reset} = useHouseholdAssistSearch();

  const properties = me?.properties ?? [];
  const trimmed = query.trim();
  const canSubmit = trimmed.length >= HOUSEHOLD_ASSIST_MIN_QUERY_LENGTH && !isPending && !isRateLimited;

  const hasResultsPanelContent =
    isPending || Boolean(validationError) || isRateLimited || Boolean(lastResult);

  const showResultsPanel = hasResultsPanelContent && !resultsDismissed;

  const needsMinLength =
    trimmed.length > 0 && trimmed.length < HOUSEHOLD_ASSIST_MIN_QUERY_LENGTH;

  const searchBlockedTooltip = needsMinLength
    ? MIN_QUERY_TOOLTIP
    : isRateLimited
      ? "Search limit reached — wait a moment before trying again."
      : null;

  const dismissResultsPanel = useCallback(() => {
    setResultsDismissed(true);
  }, []);

  useEffect(() => {
    if (!minLengthTipOpen) return;
    const timeoutId = window.setTimeout(() => setMinLengthTipOpen(false), 2500);
    return () => window.clearTimeout(timeoutId);
  }, [minLengthTipOpen]);

  useEffect(() => {
    if (!showResultsPanel) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (finderRef.current?.contains(target)) return;
      dismissResultsPanel();
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [dismissResultsPanel, showResultsPanel]);

  const handleSelect = useCallback(
    (candidate: HouseholdAssistCandidate) => {
      if (!me) return;
      const applied = applyHouseholdAssistCandidate({
        candidate,
        properties,
        currentProperty: userProperty,
        me,
        pathname: location.pathname,
        navigate,
      });
      if (applied) {
        setLastResult(null);
        setQuery("");
        setResultsDismissed(false);
      }
    },
    [location.pathname, me, navigate, properties, userProperty],
  );

  useEffect(() => {
    if (
      matchedHousehold &&
      userProperty?.id &&
      String(matchedHousehold.property_id) !== String(userProperty.id)
    ) {
      useDispatchDeskHouseholdMatchStore.getState().clearMatch();
    }
  }, [matchedHousehold, userProperty?.id]);

  const handleSearch = useCallback(async () => {
    if (needsMinLength) {
      setMinLengthTipOpen(true);
      return;
    }
    if (!canSubmit) return;
    reset();
    setResultsDismissed(false);
    const result = await search(trimmed);
    if (!result) return;
    setLastResult(result);
    if (result.status === "unique" && result.candidates[0]) {
      handleSelect(result.candidates[0]);
    }
  }, [canSubmit, handleSelect, needsMinLength, reset, search, trimmed]);

  if (!me) return null;

  if (!properties.length) {
    if (!showStartShiftHint) return null;
    return (
      <p className={cn("text-muted-foreground text-sm", className)}>
        {DISPATCH_DESK_START_SHIFT_MESSAGE}
      </p>
    );
  }

  return (
    <div ref={finderRef} className={cn("relative w-full", className)}>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setLastResult(null);
              setResultsDismissed(false);
              if (e.target.value.trim().length >= HOUSEHOLD_ASSIST_MIN_QUERY_LENGTH) {
                setMinLengthTipOpen(false);
              }
              reset();
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                dismissResultsPanel();
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                void handleSearch();
              }
            }}
            placeholder="What the caller said — address, name, PIN…"
            className="pl-9"
            aria-label="Household lookup from call"
            aria-expanded={showResultsPanel}
            aria-haspopup="listbox"
            autoComplete="off"
          />
        </div>
        {searchBlockedTooltip && !canSubmit ? (
          <Tooltip
            open={minLengthTipOpen ? true : undefined}
            onOpenChange={(open) => {
              if (!open) setMinLengthTipOpen(false);
            }}
          >
            <TooltipTrigger
              render={
                <span className="inline-flex shrink-0">
                  <Button type="button" size="sm" disabled onClick={() => void handleSearch()}>
                    Search
                  </Button>
                </span>
              }
            />
            <TooltipContent side="bottom" className="max-w-xs text-center">
              {searchBlockedTooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button type="button" size="sm" disabled={!canSubmit} onClick={() => void handleSearch()}>
            Search
          </Button>
        )}
      </div>

      {showResultsPanel ? (
        <div className={FINDER_RESULTS_PANEL_CLASS} role="listbox" aria-label="Household search results">
          {validationError ? <p className="text-destructive text-xs">{validationError}</p> : null}

          {isRateLimited ? (
            <p className="text-muted-foreground text-xs">
              Search limit reached — wait a moment before trying again.
            </p>
          ) : null}

          {isPending ? <Loader className="h-12" size="sm" /> : null}

          {!isPending && lastResult ? (
            <AssistResultsPanel result={lastResult} onSelect={handleSelect} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** @deprecated Use `DispatchHouseholdFinder`. */
export const DispatchPropertyFinder = DispatchHouseholdFinder;

/** @deprecated Use `DispatchHouseholdFinder`. */
export const DispatchHomePropertySwitcher = DispatchHouseholdFinder;
